import { LucideIcon, FolderPlus, Edit, FolderOpen, Upload, MessageSquare, FileText, LayoutTemplate, History, Share2, Bot, Wand2, Calculator, Settings, User, CreditCard, FileSignature, Palette, Bell, Plus, Check, Search, Save, Printer, Copy, Download, Users, Lock, Briefcase, PenTool, Sparkles, Scale, Home, ArrowRight, MousePointer, Folder, Eye, Trash2, MoreHorizontal } from "lucide-react";

export interface FeatureStep {
  order: number;
  title: string;
  instruction: string;
  icon: LucideIcon;
  tip?: string;
}

export interface Functionality {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  category: string;
  steps?: FeatureStep[];
  tips?: string[];
  mockupId?: string;
}

export const DOCS_FUNCTIONALITIES: Functionality[] = [
  // === INÍCIO ===
  {
    id: "welcome",
    title: "Bem-vindo ao Justiçai",
    description: "O Justiçai é a plataforma de IA 100% focada no Direito Brasileiro. Aqui você encontra ferramentas inteligentes para gerenciar casos, criar documentos jurídicos, fazer cálculos e muito mais.",
    icon: Home,
    category: "getting-started",
    mockupId: "welcome",
    tips: [
      "O Justiçai foi desenvolvido especialmente para advogados brasileiros",
      "A IA conhece legislação, jurisprudência e doutrina do Brasil",
      "Seus dados estão protegidos e são confidenciais",
    ],
  },
  {
    id: "first-steps",
    title: "Primeiros Passos",
    description: "Aprenda a configurar sua conta e começar a usar o Justiçai de forma eficiente.",
    icon: ArrowRight,
    category: "getting-started",
    mockupId: "first-steps",
    steps: [
      { order: 1, title: "Faça login na plataforma", instruction: "Acesse o Justiçai com seu email e senha cadastrados.", icon: User },
      { order: 2, title: "Complete seu perfil", instruction: "Vá em Configurações e preencha seus dados profissionais (nome, OAB, escritório).", icon: Settings, tip: "Essas informações aparecem nos documentos que você gerar." },
      { order: 3, title: "Configure seu papel timbrado", instruction: "Em Configurações > Papel Timbrado, adicione o logo e dados do seu escritório.", icon: FileSignature },
      { order: 4, title: "Crie seu primeiro caso", instruction: "Vá em Casos / Clientes e clique em 'Novo Caso' para começar.", icon: FolderPlus },
    ],
    tips: [
      "Explore cada seção da sidebar para conhecer todas as funcionalidades",
      "Use a barra de pesquisa para encontrar rapidamente o que precisa",
    ],
  },
  {
    id: "interface",
    title: "Conhecendo a Interface",
    description: "Entenda os principais elementos da tela do Justiçai e como navegar pela plataforma.",
    icon: Eye,
    category: "getting-started",
    mockupId: "interface-overview",
    steps: [
      { order: 1, title: "Sidebar (Menu Lateral)", instruction: "A sidebar à esquerda contém todas as seções principais: Casos, Chat Jurídico, Calculadoras, Configurações e mais.", icon: Folder },
      { order: 2, title: "Área Principal", instruction: "O conteúdo principal aparece no centro da tela. É onde você trabalha com casos, documentos e chats.", icon: FileText },
      { order: 3, title: "Barra Superior", instruction: "No topo você encontra a barra de pesquisa, notificações e acesso rápido ao seu perfil.", icon: Search },
      { order: 4, title: "Ações Rápidas", instruction: "Botões de ação (criar, salvar, compartilhar) geralmente ficam no canto superior direito de cada página.", icon: MousePointer },
    ],
  },
  {
    id: "sidebar",
    title: "Entendendo a Sidebar",
    description: "Conheça cada item do menu lateral e para que serve.",
    icon: Folder,
    category: "getting-started",
    mockupId: "sidebar",
    steps: [
      { order: 1, title: "Casos / Clientes", instruction: "Aqui você gerencia todos os seus processos e clientes. Cada caso pode ter documentos, chat com IA e base de conhecimento.", icon: Briefcase },
      { order: 2, title: "Chat Jurídico", instruction: "Converse com a IA Laib 3.7 sobre qualquer tema jurídico. Tire dúvidas, peça análises e muito mais.", icon: MessageSquare },
      { order: 3, title: "Assessor", instruction: "Seu assistente virtual personalizado que conhece seus casos e documentos.", icon: Bot },
      { order: 4, title: "Melhore seu Texto", instruction: "Cole um texto jurídico e a IA irá melhorar a escrita, gramática e argumentação.", icon: Wand2 },
      { order: 5, title: "Cálculos Jurídicos", instruction: "Calculadoras especializadas: trabalhistas, INSS, pensão, aluguel e mais.", icon: Calculator },
      { order: 6, title: "Configurações", instruction: "Gerencie seu perfil, plano, créditos e personalize a plataforma.", icon: Settings },
    ],
  },

  // === CASOS ===
  {
    id: "criar-caso",
    title: "Criar um Caso",
    description: "Aprenda a criar um novo caso para organizar seus processos jurídicos.",
    icon: FolderPlus,
    category: "casos",
    mockupId: "casos-header",
    steps: [
      { order: 1, title: "Acesse a página de Casos", instruction: "Na sidebar esquerda, clique em 'Casos / Clientes'.", icon: Briefcase },
      { order: 2, title: "Clique em 'Novo Caso'", instruction: "No canto superior direito, clique no botão 'Novo Caso'.", icon: Plus, tip: "Você também pode criar casos dentro de pastas específicas." },
      { order: 3, title: "Preencha as informações", instruction: "Digite o nome do caso (ex: 'João Silva - Trabalhista'). Você pode adicionar descrição e escolher um ícone personalizado.", icon: Edit },
      { order: 4, title: "Salve o caso", instruction: "Clique em 'Criar Caso'. Pronto! Seu caso foi criado e você pode começar a adicionar documentos.", icon: Check },
    ],
    tips: [
      "Use nomes descritivos para encontrar facilmente seus casos depois",
      "Você pode personalizar a cor e o ícone de cada caso",
      "Casos podem ser organizados em pastas para melhor organização",
    ],
  },
  {
    id: "editar-caso",
    title: "Editar um Caso",
    description: "Saiba como modificar as informações de um caso existente.",
    icon: Edit,
    category: "casos",
    mockupId: "caso-card",
    steps: [
      { order: 1, title: "Abra o caso desejado", instruction: "Na lista de casos, clique no caso que deseja editar.", icon: Briefcase },
      { order: 2, title: "Acesse as configurações", instruction: "Clique no ícone de três pontos (⋯) ou no botão 'Editar' no topo da página do caso.", icon: MoreHorizontal },
      { order: 3, title: "Modifique as informações", instruction: "Altere o nome, descrição, ícone ou cor do caso conforme necessário.", icon: Edit },
      { order: 4, title: "Salve as alterações", instruction: "Clique em 'Salvar' para confirmar as mudanças.", icon: Save },
    ],
  },
  {
    id: "pastas",
    title: "Organizar em Pastas",
    description: "Crie pastas para organizar seus casos por cliente, área ou como preferir.",
    icon: FolderOpen,
    category: "casos",
    mockupId: "folder-card",
    steps: [
      { order: 1, title: "Acesse a página de Casos", instruction: "Na sidebar, clique em 'Casos / Clientes'.", icon: Briefcase },
      { order: 2, title: "Crie uma nova pasta", instruction: "Clique no botão 'Nova Pasta' (ícone de pasta com +).", icon: FolderPlus },
      { order: 3, title: "Nomeie a pasta", instruction: "Digite um nome para a pasta (ex: 'Clientes 2024', 'Trabalhistas', 'Família').", icon: Edit },
      { order: 4, title: "Mova casos para a pasta", instruction: "Arraste e solte os casos na pasta desejada, ou use o menu de opções do caso para mover.", icon: Folder },
    ],
    tips: [
      "Pastas podem ter subpastas para organização mais detalhada",
      "Você pode personalizar a cor de cada pasta",
    ],
  },
  {
    id: "base-conhecimento",
    title: "Base de Conhecimento",
    description: "Faça upload de documentos PDF, contratos e peças processuais para a IA analisar.",
    icon: Upload,
    category: "casos",
    mockupId: "base-conhecimento",
    steps: [
      { order: 1, title: "Abra o caso desejado", instruction: "Acesse o caso onde deseja adicionar documentos.", icon: Briefcase },
      { order: 2, title: "Vá para Base de Conhecimento", instruction: "Dentro do caso, clique na aba 'Base de Conhecimento' ou no ícone de documentos.", icon: FileText },
      { order: 3, title: "Faça upload do arquivo", instruction: "Clique em 'Upload' ou arraste arquivos para a área indicada. Aceita PDF, DOC, DOCX e outros formatos.", icon: Upload, tip: "O tamanho máximo por arquivo depende do seu plano." },
      { order: 4, title: "Aguarde o processamento", instruction: "A IA irá ler e indexar o documento. Depois disso, você pode conversar sobre ele no chat.", icon: Sparkles },
    ],
    tips: [
      "Quanto mais documentos você adicionar, mais completo será o conhecimento da IA sobre o caso",
      "Você pode adicionar links de sites jurídicos também",
      "Documentos processados ficam disponíveis para o chat do caso",
    ],
  },
  {
    id: "chat-caso",
    title: "Chat do Caso com IA",
    description: "Converse com a IA sobre o caso específico, usando os documentos da base de conhecimento.",
    icon: MessageSquare,
    category: "casos",
    mockupId: "chat-caso",
    steps: [
      { order: 1, title: "Abra o caso desejado", instruction: "Acesse o caso sobre o qual deseja conversar.", icon: Briefcase },
      { order: 2, title: "Vá para o Chat", instruction: "Dentro do caso, clique na aba 'Chat' ou no ícone de conversa.", icon: MessageSquare },
      { order: 3, title: "Digite sua pergunta", instruction: "Escreva sua pergunta ou solicitação no campo de texto. A IA conhece todos os documentos do caso.", icon: PenTool, tip: "Seja específico nas perguntas para respostas mais precisas." },
      { order: 4, title: "Receba a resposta", instruction: "A IA irá responder com base nos documentos do caso e no conhecimento jurídico.", icon: Sparkles },
    ],
    tips: [
      "Pergunte sobre prazos, partes envolvidas, argumentos possíveis",
      "Peça resumos dos documentos anexados",
      "Solicite sugestões de teses e estratégias",
    ],
  },
  {
    id: "documentos",
    title: "Criar Documentos",
    description: "Gere documentos jurídicos como petições, contratos e pareceres usando IA.",
    icon: FileText,
    category: "casos",
    mockupId: "documentos-area",
    steps: [
      { order: 1, title: "Abra o caso", instruction: "Acesse o caso para o qual deseja criar um documento.", icon: Briefcase },
      { order: 2, title: "Vá para Documentos", instruction: "Clique na aba 'Documentos' ou no botão 'Criar Documento'.", icon: FileText },
      { order: 3, title: "Descreva o documento", instruction: "No campo de texto, descreva qual documento você precisa. Ex: 'Petição inicial trabalhista para reclamação de horas extras'.", icon: PenTool },
      { order: 4, title: "Aguarde a geração", instruction: "A IA irá gerar o documento com base na sua solicitação e nos dados do caso.", icon: Sparkles },
      { order: 5, title: "Revise e edite", instruction: "O documento aparece no editor. Revise, faça ajustes e personalize conforme necessário.", icon: Edit },
    ],
    tips: [
      "Quanto mais detalhes você fornecer, melhor será o documento gerado",
      "Você pode pedir para a IA ajustar trechos específicos",
      "O documento usa suas configurações de papel timbrado automaticamente",
    ],
  },
  {
    id: "templates",
    title: "Usar Templates",
    description: "Utilize modelos prontos de documentos para agilizar seu trabalho.",
    icon: LayoutTemplate,
    category: "casos",
    mockupId: "templates",
    steps: [
      { order: 1, title: "Acesse a criação de documentos", instruction: "Dentro de um caso, vá para a área de documentos.", icon: FileText },
      { order: 2, title: "Escolha 'Usar Template'", instruction: "Clique no botão 'Templates' ou 'Modelos Prontos'.", icon: LayoutTemplate },
      { order: 3, title: "Selecione o modelo", instruction: "Navegue pelas categorias e escolha o template desejado (petição, contrato, procuração, etc.).", icon: MousePointer },
      { order: 4, title: "Personalize", instruction: "O template será carregado no editor. Preencha os campos e personalize conforme o caso.", icon: Edit },
    ],
    tips: [
      "Templates economizam tempo em documentos recorrentes",
      "Você pode salvar seus próprios templates personalizados",
    ],
  },
  {
    id: "historico-docs",
    title: "Histórico de Documentos",
    description: "Acesse todos os documentos já criados em um caso.",
    icon: History,
    category: "casos",
    mockupId: "historico-docs",
    steps: [
      { order: 1, title: "Abra o caso", instruction: "Acesse o caso cujos documentos deseja ver.", icon: Briefcase },
      { order: 2, title: "Vá para Histórico", instruction: "Clique na aba 'Histórico' ou 'Documentos' na barra lateral do caso.", icon: History },
      { order: 3, title: "Visualize os documentos", instruction: "Todos os documentos criados aparecem em lista, ordenados por data.", icon: FileText },
      { order: 4, title: "Ações disponíveis", instruction: "Você pode visualizar, editar, copiar, baixar em PDF ou excluir cada documento.", icon: MoreHorizontal },
    ],
    tips: [
      "Use a busca para encontrar documentos específicos",
      "Documentos podem ser compartilhados diretamente do histórico",
    ],
  },
  {
    id: "compartilhar-caso",
    title: "Compartilhar Caso",
    description: "Compartilhe casos com colegas de escritório ou outros advogados.",
    icon: Share2,
    category: "casos",
    mockupId: "compartilhar",
    steps: [
      { order: 1, title: "Abra o caso", instruction: "Acesse o caso que deseja compartilhar.", icon: Briefcase },
      { order: 2, title: "Clique em Compartilhar", instruction: "No canto superior direito, clique no botão 'Compartilhar' (ícone de compartilhamento).", icon: Share2 },
      { order: 3, title: "Adicione colaboradores", instruction: "Digite o email da pessoa ou selecione de sua lista de contatos.", icon: Users },
      { order: 4, title: "Defina permissões", instruction: "Escolha o nível de acesso: apenas visualizar, editar, ou acesso completo.", icon: Lock },
      { order: 5, title: "Envie o convite", instruction: "Clique em 'Compartilhar'. A pessoa receberá um email com o convite.", icon: Check },
    ],
  },

  // === CHAT JURÍDICO ===
  {
    id: "nova-conversa",
    title: "Iniciar Conversa",
    description: "Comece uma nova conversa com a IA jurídica Laib 3.7.",
    icon: MessageSquare,
    category: "chat-juridico",
    mockupId: "chat-juridico",
    steps: [
      { order: 1, title: "Acesse o Chat Jurídico", instruction: "Na sidebar, clique em 'Chat Jurídico'.", icon: MessageSquare },
      { order: 2, title: "Inicie nova conversa", instruction: "Clique em 'Nova Conversa' ou no ícone de '+' no canto superior.", icon: Plus },
      { order: 3, title: "Digite sua pergunta", instruction: "No campo de texto, escreva sua dúvida jurídica. A IA responde sobre qualquer área do Direito Brasileiro.", icon: PenTool },
      { order: 4, title: "Receba a resposta", instruction: "A IA irá processar e responder sua pergunta com embasamento legal.", icon: Sparkles },
    ],
    tips: [
      "Seja específico nas perguntas para respostas mais precisas",
      "Você pode fazer perguntas de acompanhamento na mesma conversa",
      "A IA cita legislação, jurisprudência e doutrina quando relevante",
    ],
  },
  {
    id: "ferramentas-chat",
    title: "Ferramentas do Chat",
    description: "Conheça as opções disponíveis durante uma conversa.",
    icon: Wand2,
    category: "chat-juridico",
    mockupId: "ferramentas-chat",
    steps: [
      { order: 1, title: "Copiar resposta", instruction: "Clique no ícone de cópia para copiar o texto da resposta da IA.", icon: Copy },
      { order: 2, title: "Regenerar resposta", instruction: "Se a resposta não foi satisfatória, clique em 'Regenerar' para obter uma nova.", icon: Sparkles },
      { order: 3, title: "Salvar conversa", instruction: "Todas as conversas são salvas automaticamente no histórico.", icon: Save },
      { order: 4, title: "Renomear conversa", instruction: "Clique no título da conversa para renomeá-la e facilitar a organização.", icon: Edit },
    ],
  },
  {
    id: "anexos",
    title: "Enviar Anexos",
    description: "Envie documentos para a IA analisar durante a conversa.",
    icon: Upload,
    category: "chat-juridico",
    mockupId: "anexos",
    steps: [
      { order: 1, title: "Clique no ícone de anexo", instruction: "No campo de mensagem, clique no ícone de clip/anexo.", icon: Upload },
      { order: 2, title: "Selecione o arquivo", instruction: "Escolha o documento do seu computador (PDF, DOC, imagem, etc.).", icon: FileText },
      { order: 3, title: "Aguarde o upload", instruction: "O arquivo será enviado e processado pela IA.", icon: Sparkles },
      { order: 4, title: "Faça perguntas sobre o documento", instruction: "Agora você pode perguntar sobre o conteúdo do documento enviado.", icon: MessageSquare },
    ],
    tips: [
      "Você pode enviar múltiplos arquivos em uma conversa",
      "A IA pode comparar documentos se você enviar mais de um",
    ],
  },
  {
    id: "historico-chat",
    title: "Histórico de Conversas",
    description: "Acesse conversas anteriores e continue de onde parou.",
    icon: History,
    category: "chat-juridico",
    mockupId: "historico-chat",
    steps: [
      { order: 1, title: "Veja a lista de conversas", instruction: "Na página do Chat Jurídico, a sidebar esquerda mostra todas as suas conversas.", icon: History },
      { order: 2, title: "Clique para abrir", instruction: "Clique em qualquer conversa para visualizá-la e continuar.", icon: MousePointer },
      { order: 3, title: "Use a busca", instruction: "Use a barra de pesquisa para encontrar conversas por palavra-chave.", icon: Search },
      { order: 4, title: "Organize em pastas", instruction: "Você pode organizar conversas em pastas para melhor organização.", icon: Folder },
    ],
  },

  // === ASSESSOR IDOC ===
  {
    id: "o-que-e",
    title: "O que é o Assessor",
    description: "Entenda o Assessor, seu assistente virtual personalizado.",
    icon: Bot,
    category: "assessor",
    mockupId: "assessor",
    tips: [
      "O Assessor é como um secretário virtual que conhece todos os seus casos",
      "Ele pode responder perguntas sobre seus processos, documentos e clientes",
      "Você define exatamente o que ele pode ou não acessar",
      "Ideal para ter respostas rápidas sobre seu trabalho",
    ],
  },
  {
    id: "configurar-assessor",
    title: "Configurar o Assessor",
    description: "Personalize seu Assessor com nome, avatar e comportamento.",
    icon: Settings,
    category: "assessor",
    mockupId: "assessor-config",
    steps: [
      { order: 1, title: "Acesse as configurações", instruction: "Vá em Configurações > Assessor, ou clique no ícone de configuração na página do Assessor.", icon: Settings },
      { order: 2, title: "Defina o nome", instruction: "Escolha um nome para seu assistente (ex: 'Maria', 'Assistente Jurídico').", icon: Edit },
      { order: 3, title: "Escolha o avatar", instruction: "Selecione uma imagem ou ícone para representar seu assessor.", icon: User },
      { order: 4, title: "Ative o assessor", instruction: "Marque a opção 'Ativar Assessor' para começar a usar.", icon: Check },
    ],
  },
  {
    id: "escopo",
    title: "Definir Escopo de Acesso",
    description: "Escolha quais casos e documentos o Assessor pode acessar.",
    icon: Lock,
    category: "assessor",
    mockupId: "escopo-assessor",
    steps: [
      { order: 1, title: "Acesse configurações do Assessor", instruction: "Vá em Configurações > Assessor.", icon: Settings },
      { order: 2, title: "Escolha o modo de escopo", instruction: "Selecione: 'Todos os casos', 'Casos selecionados' ou 'Nenhum acesso a casos'.", icon: Eye },
      { order: 3, title: "Selecione casos específicos", instruction: "Se escolheu 'Casos selecionados', marque quais casos o assessor pode ver.", icon: Check },
      { order: 4, title: "Salve as configurações", instruction: "Clique em 'Salvar' para aplicar o escopo definido.", icon: Save },
    ],
    tips: [
      "Casos confidenciais podem ficar fora do escopo do assessor",
      "Você pode alterar o escopo a qualquer momento",
    ],
  },
  {
    id: "tom-resposta",
    title: "Tom de Resposta",
    description: "Defina como o Assessor deve se comunicar com você.",
    icon: MessageSquare,
    category: "assessor",
    mockupId: "tom-resposta",
    steps: [
      { order: 1, title: "Acesse configurações", instruction: "Vá em Configurações > Assessor.", icon: Settings },
      { order: 2, title: "Escolha o tom", instruction: "Selecione entre: 'Formal', 'Profissional', 'Amigável' ou 'Direto'.", icon: MessageSquare },
      { order: 3, title: "Salve", instruction: "As respostas do assessor seguirão o tom escolhido.", icon: Save },
    ],
  },

  // === MELHORE SEU TEXTO ===
  {
    id: "como-usar-melhoria",
    title: "Como Usar",
    description: "Aprenda a usar a ferramenta de melhoria de texto jurídico.",
    icon: Wand2,
    category: "melhore-texto",
    mockupId: "melhore-texto",
    steps: [
      { order: 1, title: "Acesse a ferramenta", instruction: "Na sidebar, clique em 'Melhore seu Texto'.", icon: Wand2 },
      { order: 2, title: "Cole seu texto", instruction: "No campo de texto, cole o trecho jurídico que deseja melhorar.", icon: PenTool },
      { order: 3, title: "Escolha o tipo de melhoria", instruction: "Selecione o que deseja: revisar gramática, melhorar argumentação, simplificar, formalizar, etc.", icon: Sparkles },
      { order: 4, title: "Receba o texto melhorado", instruction: "A IA irá processar e devolver seu texto aprimorado.", icon: Check },
      { order: 5, title: "Copie o resultado", instruction: "Clique em 'Copiar' para usar o texto melhorado onde precisar.", icon: Copy },
    ],
  },
  {
    id: "tipos-melhoria",
    title: "Tipos de Melhoria",
    description: "Conheça as opções de melhoria disponíveis.",
    icon: Sparkles,
    category: "melhore-texto",
    mockupId: "tipos-melhoria",
    tips: [
      "Revisar Gramática: corrige erros ortográficos e gramaticais",
      "Melhorar Argumentação: fortalece os argumentos jurídicos",
      "Simplificar: torna o texto mais claro e acessível",
      "Formalizar: deixa o texto mais formal e técnico",
      "Resumir: cria uma versão resumida do texto",
      "Expandir: desenvolve mais os argumentos apresentados",
    ],
  },

  // === CÁLCULOS ===
  {
    id: "trabalhistas",
    title: "Cálculos Trabalhistas",
    description: "Calcule rescisões, férias, horas extras e outros direitos trabalhistas.",
    icon: Calculator,
    category: "calculos",
    mockupId: "calc-trabalhista",
    steps: [
      { order: 1, title: "Acesse Cálculos Jurídicos", instruction: "Na sidebar, clique em 'Cálculos Jurídicos'.", icon: Calculator },
      { order: 2, title: "Selecione 'Trabalhistas'", instruction: "Escolha a categoria 'Cálculos Trabalhistas'.", icon: Scale },
      { order: 3, title: "Escolha o tipo de cálculo", instruction: "Selecione: Rescisão, Férias, 13º Salário, Horas Extras, etc.", icon: MousePointer },
      { order: 4, title: "Preencha os dados", instruction: "Informe salário, datas, tipo de demissão e outros dados solicitados.", icon: Edit },
      { order: 5, title: "Veja o resultado", instruction: "O sistema calcula automaticamente todos os valores devidos.", icon: Check },
    ],
  },
  {
    id: "inss",
    title: "Cálculos INSS",
    description: "Calcule contribuições previdenciárias e benefícios.",
    icon: Calculator,
    category: "calculos",
    mockupId: "calc-inss",
    steps: [
      { order: 1, title: "Acesse Cálculos Jurídicos", instruction: "Na sidebar, clique em 'Cálculos Jurídicos'.", icon: Calculator },
      { order: 2, title: "Selecione 'INSS'", instruction: "Escolha a categoria 'Cálculos INSS'.", icon: Scale },
      { order: 3, title: "Escolha o tipo", instruction: "Selecione o tipo de cálculo: contribuições, aposentadoria, etc.", icon: MousePointer },
      { order: 4, title: "Preencha os dados", instruction: "Informe os dados solicitados conforme o tipo de cálculo.", icon: Edit },
    ],
  },
  {
    id: "atualizacao",
    title: "Atualização de Valores",
    description: "Corrija valores monetários com índices oficiais (INPC, IPCA, SELIC, etc.).",
    icon: Calculator,
    category: "calculos",
    mockupId: "calc-atualizacao",
    steps: [
      { order: 1, title: "Acesse Cálculos Jurídicos", instruction: "Na sidebar, clique em 'Cálculos Jurídicos'.", icon: Calculator },
      { order: 2, title: "Selecione 'Atualização'", instruction: "Escolha 'Atualização de Valores'.", icon: Scale },
      { order: 3, title: "Informe o valor e datas", instruction: "Digite o valor original, data inicial e data final.", icon: Edit },
      { order: 4, title: "Escolha o índice", instruction: "Selecione o índice de correção: INPC, IPCA, SELIC, TR, etc.", icon: MousePointer },
      { order: 5, title: "Calcule", instruction: "Clique em 'Calcular' para ver o valor atualizado.", icon: Check },
    ],
  },
  {
    id: "pensao",
    title: "Pensão Alimentícia",
    description: "Calcule valores de pensão alimentícia.",
    icon: Calculator,
    category: "calculos",
    mockupId: "calc-pensao",
  },
  {
    id: "aluguel",
    title: "Cálculos de Aluguel",
    description: "Calcule reajustes, multas e valores de locação.",
    icon: Calculator,
    category: "calculos",
    mockupId: "calc-aluguel",
  },
  {
    id: "dosimetria",
    title: "Dosimetria de Pena",
    description: "Calcule dosimetria penal conforme o Código Penal.",
    icon: Scale,
    category: "calculos",
    mockupId: "calc-dosimetria",
  },

  // === CONFIGURAÇÕES ===
  {
    id: "perfil",
    title: "Meu Perfil",
    description: "Gerencie seus dados pessoais e profissionais.",
    icon: User,
    category: "configuracoes",
    mockupId: "configuracoes",
    steps: [
      { order: 1, title: "Acesse Configurações", instruction: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em 'Meu Perfil'", instruction: "Clique na aba 'Perfil' ou 'Dados Pessoais'.", icon: User },
      { order: 3, title: "Edite suas informações", instruction: "Atualize nome, email, telefone, número da OAB e escritório.", icon: Edit },
      { order: 4, title: "Salve as alterações", instruction: "Clique em 'Salvar' para confirmar.", icon: Save },
    ],
    tips: [
      "Seus dados de OAB aparecem nos documentos gerados",
      "Mantenha seu email atualizado para receber notificações",
    ],
  },
  {
    id: "planos",
    title: "Planos e Assinatura",
    description: "Veja seu plano atual e faça upgrade se necessário.",
    icon: CreditCard,
    category: "configuracoes",
    mockupId: "planos-creditos",
    steps: [
      { order: 1, title: "Acesse Configurações", instruction: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em 'Planos'", instruction: "Clique na aba 'Planos' ou 'Assinatura'.", icon: CreditCard },
      { order: 3, title: "Veja seu plano atual", instruction: "Visualize os recursos do seu plano e quanto já usou.", icon: Eye },
      { order: 4, title: "Faça upgrade", instruction: "Se precisar de mais recursos, clique em 'Fazer Upgrade' e escolha um plano maior.", icon: ArrowRight },
    ],
    tips: [
      "Planos maiores oferecem mais créditos de IA e armazenamento",
      "O upgrade é imediato e você pode usar os recursos na hora",
    ],
  },
  {
    id: "creditos",
    title: "Créditos e Armazenamento",
    description: "Compre créditos extras de IA e espaço de armazenamento.",
    icon: CreditCard,
    category: "configuracoes",
    mockupId: "planos-creditos",
    steps: [
      { order: 1, title: "Acesse Configurações", instruction: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em 'Créditos'", instruction: "Clique na aba 'Créditos' ou 'Comprar Créditos'.", icon: CreditCard },
      { order: 3, title: "Veja seu saldo", instruction: "Visualize quantos créditos você ainda tem disponíveis.", icon: Eye },
      { order: 4, title: "Compre mais", instruction: "Escolha um pacote de créditos e clique em 'Comprar'.", icon: Plus },
    ],
    tips: [
      "Créditos são usados para conversas com IA e geração de documentos",
      "Comprar pacotes maiores geralmente é mais econômico",
    ],
  },
  {
    id: "papel-timbrado",
    title: "Papel Timbrado",
    description: "Configure o cabeçalho e rodapé dos seus documentos.",
    icon: FileSignature,
    category: "configuracoes",
    mockupId: "papel-timbrado",
    steps: [
      { order: 1, title: "Acesse Configurações", instruction: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em 'Papel Timbrado'", instruction: "Clique na aba 'Papel Timbrado' ou 'Documentos'.", icon: FileSignature },
      { order: 3, title: "Adicione seu logo", instruction: "Faça upload da logo do seu escritório.", icon: Upload },
      { order: 4, title: "Configure o cabeçalho", instruction: "Adicione nome do escritório, endereço, telefone e outras informações.", icon: Edit },
      { order: 5, title: "Configure o rodapé", instruction: "Adicione OAB, email e informações de contato no rodapé.", icon: Edit },
      { order: 6, title: "Salve", instruction: "Clique em 'Salvar'. Todos os documentos gerados usarão esse papel timbrado.", icon: Save },
    ],
  },
  {
    id: "tema",
    title: "Tema Claro/Escuro",
    description: "Escolha a aparência visual da plataforma.",
    icon: Palette,
    category: "configuracoes",
    mockupId: "tema",
    steps: [
      { order: 1, title: "Acesse Configurações", instruction: "Na sidebar, clique em 'Configurações'.", icon: Settings },
      { order: 2, title: "Vá em 'Aparência'", instruction: "Clique na aba 'Aparência' ou 'Tema'.", icon: Palette },
      { order: 3, title: "Escolha o tema", instruction: "Selecione 'Claro', 'Escuro' ou 'Automático' (segue o sistema).", icon: MousePointer },
    ],
  },

  // === COMPARTILHAMENTO ===
  {
    id: "como-compartilhar",
    title: "Compartilhar um Caso",
    description: "Envie um caso para um colega visualizar ou editar.",
    icon: Share2,
    category: "compartilhamento",
    mockupId: "compartilhar",
    steps: [
      { order: 1, title: "Abra o caso", instruction: "Acesse o caso que deseja compartilhar.", icon: Briefcase },
      { order: 2, title: "Clique em Compartilhar", instruction: "No topo da página, clique no botão 'Compartilhar'.", icon: Share2 },
      { order: 3, title: "Adicione pessoas", instruction: "Digite o email ou selecione da lista de contatos.", icon: Users },
      { order: 4, title: "Defina permissões", instruction: "Escolha: 'Visualizar', 'Editar' ou 'Acesso Total'.", icon: Lock },
      { order: 5, title: "Envie", instruction: "Clique em 'Compartilhar'. A pessoa receberá o convite por email.", icon: Check },
    ],
  },
  {
    id: "grupos",
    title: "Criar Grupos",
    description: "Crie grupos de trabalho para compartilhar com várias pessoas.",
    icon: Users,
    category: "compartilhamento",
    mockupId: "grupos",
    steps: [
      { order: 1, title: "Acesse Compartilhamento", instruction: "Na sidebar, clique em 'Compartilhamento' ou vá em Configurações.", icon: Share2 },
      { order: 2, title: "Crie um grupo", instruction: "Clique em 'Novo Grupo' e dê um nome (ex: 'Equipe Trabalhista').", icon: Plus },
      { order: 3, title: "Adicione membros", instruction: "Adicione as pessoas que fazem parte do grupo.", icon: Users },
      { order: 4, title: "Use o grupo", instruction: "Ao compartilhar casos ou conversas, você pode selecionar o grupo inteiro.", icon: Check },
    ],
  },
  {
    id: "permissoes",
    title: "Gerenciar Permissões",
    description: "Controle quem pode ver e editar seus casos.",
    icon: Lock,
    category: "compartilhamento",
    mockupId: "permissoes",
    steps: [
      { order: 1, title: "Acesse o caso compartilhado", instruction: "Abra o caso cujas permissões deseja gerenciar.", icon: Briefcase },
      { order: 2, title: "Clique em Compartilhar", instruction: "Veja a lista de pessoas com acesso.", icon: Share2 },
      { order: 3, title: "Altere permissões", instruction: "Clique no nome da pessoa e mude o nível de acesso.", icon: Edit },
      { order: 4, title: "Remova acesso", instruction: "Para remover alguém, clique no ícone de lixeira ao lado do nome.", icon: Trash2 },
    ],
    tips: [
      "'Visualizar' permite apenas ver o caso",
      "'Editar' permite modificar documentos",
      "'Acesso Total' permite tudo, inclusive compartilhar com outros",
    ],
  },

];

export const getFunctionalityById = (id: string): Functionality | undefined => {
  return DOCS_FUNCTIONALITIES.find(f => f.id === id);
};

export const getFunctionalitiesByCategory = (category: string): Functionality[] => {
  return DOCS_FUNCTIONALITIES.filter(f => f.category === category);
};
